// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { Form } from 'antd';
import { Store } from 'antd/lib/form/interface';
import { FormInstance } from 'antd';
import {
    BarWidgetType,
    CustomizeOutput,
    WidgetIntervalType,
    WidgetOutput,
    FilterInput,
    ObjectTypeV1,
} from 'thehive-sdk';
import DividerBasics from '@Organisms/Dashboard/WidgetManage/WidgetFormCommon/DividerBasics';
import DividerFilter from '@Organisms/Dashboard/WidgetManage/WidgetFormCommon/DividerFilter';
import { useLocalMessage } from '@Helpers/';
import ProvidedWidgetFormFilter from '@Organisms/Dashboard/WidgetManage/WidgetFormFilter/ProvidedWidgetFormFilter';
import TitleField from '@Organisms/Dashboard/WidgetManage/WidgetFormField/TitleField';
import IntervalField from '@Organisms/Dashboard/WidgetManage/WidgetFormField/IntervalField';
import EntityField from '@Organisms/Dashboard/WidgetManage/WidgetFormField/EntityField';
import { THForm } from '@Molecules/THForm/THForm';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { CustomizeField } from '../CustomizeField';
import { useCustomize } from '../utils';
import { entitiesToExclude } from '@Constants/dashboard';

export interface BarValues extends Store {
    title: string;
    interval: WidgetIntervalType;
    entity: ObjectTypeV1;
    dateField: string;
    category: string;
    stacked: boolean;
    filters: FilterInput[];
    periodDateField?: string;
    log?: boolean;
    customize?: CustomizeOutput[];
}

interface BarFormProps {
    onFinish: (values: BarValues) => void;
    defaultValues?: WidgetOutput & { options: BarWidgetType };
    form: FormInstance;
    onCancel: () => void;
    onChange: () => void;
    isValid?: boolean;
}

export const BarForm = ({ onFinish, defaultValues, form, onCancel, onChange }: BarFormProps): ReactElement | null => {
    const i = useLocalMessage();

    const entity = Form.useWatch(['entity'], form);
    const category = Form.useWatch(['category'], form);

    useCustomize({ entity, field: category, form });

    return (
        <THForm<BarValues>
            name="bar-form"
            form={form}
            onFinish={onFinish}
            onValuesChange={onChange}
            initialValues={{
                title: defaultValues?.options.title,
                interval: defaultValues?.options.interval,
                entity: defaultValues?.options.entity,
                dateField: defaultValues?.options.dateField,
                periodDateField: defaultValues?.options.periodDateField || '_createdAt',
                category: defaultValues?.options.field,
                stacked: defaultValues?.options.stacked,
                filters: defaultValues?.options.filters,
                log: defaultValues?.options.log,
                customize: defaultValues?.options.customize,
            }}
        >
            <THForm.Layout
                footer={<FormSubmitButton onCancel={onCancel} disabled={false} footerText={i('general.confirm')} />}
            >
                <DividerBasics />
                <TitleField />
                <IntervalField />
                <EntityField form={form} showCategory showDate showPeriodDate entitiesToExclude={entitiesToExclude} />

                <THForm.Item name="stacked" label={i('dashboard.stack-type')} valuePropName="checked">
                    <THSwitch />
                </THForm.Item>

                <THForm.Item name="log" label={i('dashboard.log')} valuePropName="checked">
                    <THSwitch />
                </THForm.Item>

                <DividerFilter />

                <Form.Item name="filters" className="form-item no-value">
                    <ProvidedWidgetFormFilter entity={entity} />
                </Form.Item>

                <CustomizeField />
            </THForm.Layout>
        </THForm>
    );
};
